import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch, type ApiError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import {
  eventTypesResponseSchema,
  subscriptionSchema,
  subscriptionsResponseSchema,
  type EventType,
  type NewSubscription,
  type Subscription,
} from "./schemas";

function base(tenantID: string): string {
  return `/api/v1/tenants/${encodeURIComponent(tenantID)}/notifications`;
}

export async function fetchEventTypes(tenantID: string): Promise<EventType[]> {
  const raw = await apiFetch<unknown>(`${base(tenantID)}/event-types`);
  return eventTypesResponseSchema.parse(raw).data;
}

export function useEventTypesQuery(tenantID: string) {
  return useQuery<EventType[], ApiError>({
    queryKey: queryKeys.notifications.eventTypes(tenantID),
    queryFn: () => fetchEventTypes(tenantID),
    enabled: tenantID.length > 0,
    staleTime: Infinity,
  });
}

export async function fetchSubscriptions(tenantID: string): Promise<Subscription[]> {
  const raw = await apiFetch<unknown>(`${base(tenantID)}/subscriptions`);
  return subscriptionsResponseSchema.parse(raw).data;
}

export function useSubscriptionsQuery(tenantID: string) {
  return useQuery<Subscription[], ApiError>({
    queryKey: queryKeys.notifications.subscriptions(tenantID),
    queryFn: () => fetchSubscriptions(tenantID),
    enabled: tenantID.length > 0,
  });
}

function toWire(input: NewSubscription) {
  const recipient: Record<string, unknown> = { kind: input.recipient_kind };
  switch (input.recipient_kind) {
    case "role":
      recipient.role = input.recipient_role;
      break;
    case "admin":
      recipient.admin_id = input.recipient_admin_id;
      break;
    case "email_external":
      recipient.email = input.recipient_email;
      break;
  }
  return {
    event_type_pattern: input.event_type_pattern,
    recipient,
    channels: input.channels,
    severity_floor: input.severity_floor,
    enabled: true,
  };
}

export function useCreateSubscriptionMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<Subscription, ApiError, NewSubscription>({
    mutationFn: (input) =>
      apiFetch<unknown>(`${base(tenantID)}/subscriptions`, { method: "POST", body: toWire(input) }).then(
        (raw) => subscriptionSchema.parse(raw),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.notifications.subscriptions(tenantID) });
    },
  });
}

export function useSetSubscriptionEnabledMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<Subscription, ApiError, { id: string; enabled: boolean }>({
    mutationFn: ({ id, enabled }) =>
      apiFetch<unknown>(`${base(tenantID)}/subscriptions/${encodeURIComponent(id)}`, {
        method: "PATCH",
        body: { enabled },
      }).then((raw) => subscriptionSchema.parse(raw)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.notifications.subscriptions(tenantID) });
    },
  });
}

export function useDeleteSubscriptionMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<void, ApiError, string>({
    mutationFn: (id) =>
      apiFetch<void>(`${base(tenantID)}/subscriptions/${encodeURIComponent(id)}`, { method: "DELETE" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.notifications.subscriptions(tenantID) });
    },
  });
}
